import { Button } from "@aurumiq/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@aurumiq/ui/components/dialog";
import { Label } from "@aurumiq/ui/components/label";
import { Switch } from "@aurumiq/ui/components/switch";
import { CopyField } from "@aurumiq/ui/composite/copy-button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useId, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { SelectField, TextField } from "@/components/field";
import type { MemberInviteResult, MemberRole } from "@/lib/types";
import { useInviteMember } from "./queries";

export const MEMBER_ROLES: MemberRole[] = ["owner", "admin", "member"];

const schema = z.object({
  email: z.email("Enter a valid email"),
  role: z.enum(MEMBER_ROLES),
  send_invite: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

export function InviteDialog({
  orgId,
  open,
  onOpenChange,
}: {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const invite = useInviteMember(orgId);
  const [result, setResult] = useState<MemberInviteResult | null>(null);
  const switchId = useId();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", role: "member", send_invite: true },
  });
  const err = (k: keyof FormValues) => form.formState.errors[k]?.message;

  const close = (next: boolean) => {
    if (!next) {
      form.reset();
      setResult(null);
    }
    onOpenChange(next);
  };

  const onSubmit = form.handleSubmit(async (v) => {
    const r = await invite.mutateAsync(v);
    setResult(r);
  });

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        {result ? (
          <div className="grid gap-4">
            <DialogHeader>
              <DialogTitle>Invitation created</DialogTitle>
              <DialogDescription>
                {result.member.email} was added as <b>{result.member.role}</b>.
              </DialogDescription>
            </DialogHeader>
            <div>
              <p className="mb-1 font-semibold text-sm">
                Share this sign-in link with the customer
              </p>
              <CopyField value={result.invite_link} label="Copy link" />
              <p className="mt-1 text-muted-foreground text-xs">
                The link is shown once here; it can be re-issued by inviting the user again.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" onClick={() => close(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="grid gap-4">
            <DialogHeader>
              <DialogTitle>Invite user</DialogTitle>
              <DialogDescription>
                Adds a member to this organisation and returns a sign-in link.
              </DialogDescription>
            </DialogHeader>
            <TextField
              label="Email"
              type="email"
              autoComplete="off"
              error={err("email")}
              {...form.register("email")}
            />
            <SelectField label="Role" error={err("role")} {...form.register("role")}>
              {MEMBER_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </SelectField>
            <div className="flex items-center gap-2">
              <Controller
                control={form.control}
                name="send_invite"
                render={({ field }) => (
                  <Switch id={switchId} checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
              <Label htmlFor={switchId} className="mb-0">
                Send invitation email
              </Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => close(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={invite.isPending}>
                {invite.isPending ? "Inviting…" : "Invite"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
